// The Möbius strip of dyads (Tymoczko): every two-note chord is a point,
// every two-voice voice leading a path. The bottom edge (unisons) is the
// strip's boundary — paths reflect off it like a mirror. The top edge
// (tritones) is glued to itself a half-turn along: the twist. The ii–V–I's
// guide tones live right at the fold.

import { useMemo } from 'react';
import { Play } from 'lucide-react';
import { spellNote } from '@/music/notes';
import { dyadPath, reduceDyad, type DyadPoint } from '@/music/dyads';
import { getChordPlayer } from '@/audio/player';

const W = 760;
const H = 340;
const PAD = 44;

const sx = (u: number) => PAD + (u / 12) * (W - 2 * PAD);
const sy = (d: number) => H - PAD - (d / 6) * (H - 2 * PAD);
const spell = (pc: number) => spellNote(((pc % 12) + 12) % 12, 'flat');

function playDyad(u: number, d: number) {
  void getChordPlayer().playMidi(d === 0 ? [60 + u] : [60 + u, 60 + u + d], 0.02);
}

/** Break a sampled path into polylines wherever u wraps around the octave. */
function segments(points: DyadPoint[]): string[] {
  const out: string[] = [];
  let cur: string[] = [];
  let prev: DyadPoint | null = null;
  for (const p of points) {
    if (prev && Math.abs(p.u - prev.u) > 6) {
      if (cur.length > 1) out.push(cur.join(' '));
      cur = [];
    }
    cur.push(`${sx(p.u)},${sy(p.d)}`);
    prev = p;
  }
  if (cur.length > 1) out.push(cur.join(' '));
  return out;
}

// The ii–V–I guide-tone line: (C,F) → (B,F) → (B,E), one semitone per move.
const LEG1 = dyadPath([60, 65], [59, 65]);
const LEG2 = dyadPath([59, 65], [59, 64]);
const STOPS: { point: DyadPoint; label: string; midi: [number, number] }[] = [
  { point: reduceDyad(60, 65), label: 'Dm7 (C·F)', midi: [60, 65] },
  { point: reduceDyad(59, 65), label: 'G7 (B·F)', midi: [59, 65] },
  { point: reduceDyad(59, 64), label: 'Cmaj7 (B·E)', midi: [59, 64] },
];

export function MobiusExhibit() {
  const lattice = useMemo(() => {
    const out: { u: number; d: number }[] = [];
    for (let u = 0; u < 12; u++) for (let d = 0; d <= 6; d++) out.push({ u, d });
    return out;
  }, []);

  const playProgression = () => {
    STOPS.forEach((s, i) => {
      setTimeout(() => void getChordPlayer().playMidi([...s.midi], 0.02), i * 700);
    });
  };

  return (
    <div className="lab-exhibit" data-testid="lab-mobius">
      <p className="lab-lede">
        If every two-note chord is a point, and the order of the two voices is ignored, the points
        form a <strong>Möbius strip</strong>. Height is the interval. The unisons along the bottom
        are the strip&rsquo;s <em>boundary</em>, and a voice leading that reaches it reflects back.
        The tritones along the top join to themselves half a turn along, which is the twist. The
        path shows the 3rd and 7th of a ii–V–I: Dm7&rarr;G7 moves onto the tritone line and
        G7&rarr;Cmaj7 moves back off it. Click any point to hear its two notes.
      </p>

      <div className="lab-controls">
        <button className="btn btn-sm" onClick={playProgression} data-testid="mobius-play">
          <Play size={14} fill="currentColor" aria-hidden="true" />
          Play the guide-tone line
        </button>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', maxWidth: 860 }}
        role="img"
        aria-label="Möbius strip of dyads"
      >
        {/* interval guide lines */}
        {Array.from({ length: 7 }, (_, d) => (
          <g key={d}>
            <line
              x1={sx(0)}
              y1={sy(d)}
              x2={sx(12)}
              y2={sy(d)}
              stroke={d === 0 ? 'var(--ink)' : d === 6 ? 'var(--danger)' : 'var(--hairline)'}
              strokeWidth={d === 0 ? 2.4 : d === 6 ? 1.8 : 1}
              strokeDasharray={d === 6 ? '6 4' : undefined}
            />
            <text
              x={sx(12) + 8}
              y={sy(d)}
              fontSize={10}
              dominantBaseline="central"
              fill="var(--muted)"
            >
              {d === 0 ? 'unison' : d === 6 ? 'tritone' : d}
            </text>
          </g>
        ))}
        {/* the half-turn glue on the tritone edge */}
        {Array.from({ length: 6 }, (_, u) => (
          <path
            key={`tie${u}`}
            d={`M ${sx(u)} ${sy(6) - 8} Q ${sx(u + 3)} ${sy(6) - 30} ${sx(u + 6)} ${sy(6) - 8}`}
            fill="none"
            stroke="var(--danger)"
            strokeWidth={0.8}
            opacity={0.4}
          />
        ))}
        <text
          x={sx(6)}
          y={sy(6) - 32}
          textAnchor="middle"
          fontSize={10}
          fill="var(--danger)"
          opacity={0.85}
        >
          each tritone appears twice — same point, half a turn apart
        </text>

        {/* the guide-tone path */}
        {[...segments(LEG1), ...segments(LEG2)].map((pts, i) => (
          <polyline
            key={i}
            points={pts}
            fill="none"
            stroke="var(--accent)"
            strokeWidth={2.6}
            strokeLinecap="round"
            opacity={0.9}
          />
        ))}

        {/* dyad lattice */}
        {lattice.map(({ u, d }) => (
          <circle
            key={`${u}-${d}`}
            cx={sx(u)}
            cy={sy(d)}
            r={d === 0 ? 3 : 4}
            fill={d === 6 ? 'var(--danger)' : d === 0 ? 'var(--faint)' : 'var(--accent)'}
            opacity={d === 6 ? 0.85 : 0.55}
            onClick={() => playDyad(u, d)}
            style={{ cursor: 'pointer' }}
            data-testid={`dyad-${u}-${d}`}
          >
            <title>{d === 0 ? `${spell(u)} unison` : `${spell(u)} · ${spell(u + d)}`}</title>
          </circle>
        ))}

        {/* the three stops, labelled */}
        {STOPS.map((s, i) => (
          <g
            key={s.label}
            onClick={() => void getChordPlayer().playMidi([...s.midi], 0.02)}
            style={{ cursor: 'pointer' }}
          >
            <circle
              cx={sx(s.point.u)}
              cy={sy(s.point.d)}
              r={7.5}
              fill="var(--accent)"
              stroke="var(--surface)"
              strokeWidth={2}
            />
            <text
              x={sx(s.point.u)}
              y={sy(s.point.d) + (i === 1 ? -14 : 20)}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fill="var(--ink)"
            >
              {s.label}
            </text>
          </g>
        ))}

        {/* bottom-note letters */}
        {Array.from({ length: 13 }, (_, u) => (
          <text
            key={u}
            x={sx(u)}
            y={H - PAD + 18}
            textAnchor="middle"
            fontSize={10}
            fill="var(--muted)"
          >
            {spell(u)}
          </text>
        ))}
      </svg>

      <p className="muted">
        The left and right edges are the same line: the strip repeats every octave. The tritone, the
        most even two-note chord, lies on the fold. The diminished and augmented symmetry exhibit
        shows the same thing for °7 chords and augmented triads.
      </p>
    </div>
  );
}
